import React from 'react';
import PropTypes from 'prop-types';
import DataTable from './DataTable';
import Pagination from './Pagination/Pagination';

/**
 * Complete table container with pagination following the articlesTable.js pattern
 * 
 * @param {Object} props
 * @param {Array} props.columns - Array of column definitions
 * @param {Array} props.data - Array of data objects
 * @param {Function} props.onRowClick - Optional row click handler
 * @param {boolean} props.loading - Loading state
 * @param {React.Component} props.SkeletonRow - Skeleton component for loading state
 * @param {React.Component} props.EmptyState - Empty state component
 * @param {boolean} props.selectable - Whether rows are selectable
 * @param {Set} props.selectedItems - Set of selected item IDs
 * @param {Function} props.onSelectItem - Handler for item selection
 * @param {Function} props.onSelectAll - Handler for select all
 * @param {number} props.currentPage - Current page number
 * @param {number} props.totalPages - Total number of pages
 * @param {Function} props.onPageChange - Page change handler
 * @param {number} props.itemsPerPage - Items per page
 * @param {number} props.totalCount - Total count of items
 * @param {Function} props.onItemsPerPageChange - Items per page change handler
 * @param {boolean} props.showPagination - Whether to show pagination
 * @param {string} props.className - Additional CSS classes
 */
const TableContainer = ({
  columns = [],
  data = [],
  onRowClick,
  loading = false,
  SkeletonRow,
  EmptyState,
  selectable = false,
  selectedItems = new Set(),
  onSelectItem,
  onSelectAll,
  currentPage = 1,
  totalPages = 0,
  onPageChange,
  itemsPerPage = 25,
  totalCount = 0,
  onItemsPerPageChange,
  showPagination = true,
  className = '',
  tableProps = {},
  ...rest
}) => {
  return (
    <div className={`${className}`} {...rest}>
      <DataTable
        columns={columns}
        data={data}
        onRowClick={onRowClick}
        loading={loading}
        SkeletonRow={SkeletonRow}
        EmptyState={EmptyState}
        selectable={selectable}
        selectedItems={selectedItems}
        onSelectItem={onSelectItem}
        onSelectAll={onSelectAll}
        tableProps={tableProps}
        itemsPerPageSkeleton={itemsPerPage}
      />
      
      {!loading && data.length > 0 && showPagination && totalPages > 1 && (
        <Pagination 
          currentPage={currentPage} 
          totalPages={totalPages} 
          onPageChange={onPageChange}
          itemsPerPage={itemsPerPage}
          totalCount={totalCount}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      )}
    </div>
  );
};

TableContainer.displayName = 'TableContainer';

TableContainer.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  onRowClick: PropTypes.func,
  loading: PropTypes.bool,
  SkeletonRow: PropTypes.elementType,
  EmptyState: PropTypes.elementType,
  selectable: PropTypes.bool,
  selectedItems: PropTypes.instanceOf(Set),
  onSelectItem: PropTypes.func,
  onSelectAll: PropTypes.func,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func,
  itemsPerPage: PropTypes.number,
  totalCount: PropTypes.number,
  onItemsPerPageChange: PropTypes.func,
  showPagination: PropTypes.bool,
  className: PropTypes.string,
  tableProps: PropTypes.object
};

export default TableContainer;
